import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@trove/contracts';
import { resetAnalytics } from './analytics';
import { ApiClientError, api, getSession, postJson } from './api';

interface SessionContextValue {
  user: SessionUser | null;
  loading: boolean;
  error: Error | null;
  switchDemoRole(role: 'owner' | 'recipient'): Promise<void>;
  logout(): Promise<void>;
  resetDemo(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const isDemoHost = () =>
  window.location.hostname === 'demo-trove.fldr.zip' ||
  (window.location.hostname === 'localhost' &&
    new URLSearchParams(window.location.search).get('demo') === '1');

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useQuery({ queryKey: ['session'], queryFn: getSession });
  const demo = useMutation({
    mutationFn: () =>
      postJson<{ instanceId: string; recipientEmail: string }>('/v1/demo/session', {}),
    onSuccess: (data) => {
      sessionStorage.setItem('trove-demo-instance', JSON.stringify(data));
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });

  useEffect(() => {
    if (
      session.error instanceof ApiClientError &&
      session.error.status === 401 &&
      isDemoHost() &&
      !demo.isPending &&
      !demo.isSuccess &&
      !demo.isError
    )
      demo.mutate();
  }, [session.error, demo.isPending, demo.isSuccess, demo.isError]);

  const switchDemoRole = async (role: 'owner' | 'recipient') => {
    await postJson('/v1/demo/role', { role });
    await queryClient.invalidateQueries({ queryKey: ['session'] });
    await queryClient.invalidateQueries();
  };

  const logout = async () => {
    await postJson('/v1/logout', {});
    resetAnalytics();
    queryClient.clear();
    window.location.assign('/login');
  };

  const resetDemo = async () => {
    const current = session.data?.user;
    if (current) {
      const { clearWorkspace } = await import('./storage');
      await clearWorkspace(current.id);
    }
    try {
      await api('/v1/logout', { method: 'POST', body: '{}' });
    } catch (cause) {
      if (!(cause instanceof ApiClientError) || cause.status !== 401) throw cause;
    } finally {
      sessionStorage.removeItem('trove-demo-instance');
      localStorage.removeItem('trove-demo-recipient-keys');
      queryClient.clear();
      window.location.reload();
    }
  };

  return (
    <SessionContext.Provider
      value={{
        user: session.data?.user ?? null,
        loading: session.isLoading || demo.isPending,
        error:
          session.error instanceof Error &&
          !(session.error instanceof ApiClientError && session.error.status === 401)
            ? session.error
            : null,
        switchDemoRole,
        logout,
        resetDemo,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within SessionProvider');
  return context;
}
